import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Users, Smartphone, Globe, Monitor, Download, Eye, Loader2,
  ChevronLeft, ChevronRight, MapPin, Send,
} from "lucide-react";
import { toast } from "sonner";

export default function AudiencePage() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [period, setPeriod] = useState("today");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [filters, setFilters] = useState({ country: "", os: "", bundle: "" });
  const [filterOptions, setFilterOptions] = useState({ countries: [], os: [], bundles: [] });
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/audience/filters").then(r => setFilterOptions(r.data)).catch(() => {});
  }, [api]);

  const buildParams = () => {
    let p = `period=${period}`;
    if (period === "custom" && customRange.start && customRange.end)
      p = `period=custom&start_date=${customRange.start}&end_date=${customRange.end}`;
    if (filters.country) p += `&country=${filters.country}`;
    if (filters.os) p += `&os=${filters.os}`;
    if (filters.bundle) p += `&bundle=${filters.bundle}`;
    return p;
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const r = await api.get(`/audience/stats?${buildParams()}`);
      setStats(r.data);
    } catch { }
    finally { setStatsLoading(false); }
  };

  const fetchData = async (p = 1) => {
    setLoading(true);
    try {
      const r = await api.get(`/audience/data?${buildParams()}&page=${p}&page_size=50`);
      setData(r.data);
      setPage(p);
    } catch { toast.error("Failed to fetch audience data"); }
    finally { setLoading(false); }
  };

  const generate = () => { fetchStats(); fetchData(1); };

  const exportCSV = () => {
    if (!data?.data?.length) return;
    const headers = "timestamp,device_ifa,device_type,os,osv,make,model,ip,country,region,city,zip,lat,lon,language,carrier,connectiontype,ipservice,inventory_type,bundle,app_name,device_ua";
    const rows = data.data.map(d =>
      [d.timestamp?.slice(0, 19), d.device_ifa, d.device_type, d.os, d.osv || "", `"${d.make}"`, `"${d.model}"`, d.ip, d.country, d.region || "", d.city || "", d.zip || "", d.lat ?? "", d.lon ?? "", d.language || "", `"${d.carrier || ""}"`, d.connectiontype ?? "", d.ipservice ?? "", d.inventory_type, d.bundle, `"${d.app_name || ""}"`, `"${(d.device_ua || "").slice(0, 100)}"`].join(",")
    );
    const blob = new Blob([headers + "\n" + rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `audience_${period}.csv`; a.click();
    toast.success("Exported");
  };

  const f = (n) => n == null ? "0" : Number(n).toLocaleString();

  return (
    <div className="space-y-5" data-testid="audience-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="h-7 w-7 text-accent" /> Audience</h1>
          <p className="text-muted-foreground text-sm">Device & user data from bid requests (180 day retention)</p>
        </div>
      </div>

      <Tabs defaultValue="data">
        <TabsList>
          <TabsTrigger value="data" className="gap-2"><Users className="h-4 w-4" /> Audience Data</TabsTrigger>
          <TabsTrigger value="spatial" className="gap-2"><MapPin className="h-4 w-4" /> Spatial Reach API</TabsTrigger>
          <TabsTrigger value="keys" className="gap-2"><Globe className="h-4 w-4" /> API Keys</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="space-y-5">

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={period} onValueChange={v => { if (v === "custom") setPeriod("custom"); else setPeriod(v); }}>
          <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="yesterday">Yesterday</SelectItem>
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>
        {period === "custom" && (<>
          <Input type="date" value={customRange.start} onChange={e => setCustomRange({ ...customRange, start: e.target.value })} className="w-[140px] h-8 text-xs" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={customRange.end} min={customRange.start} onChange={e => setCustomRange({ ...customRange, end: e.target.value })} className="w-[140px] h-8 text-xs" />
        </>)}
        <Select value={filters.country || "all"} onValueChange={v => setFilters({ ...filters, country: v === "all" ? "" : v })}>
          <SelectTrigger className="w-[200px] h-8 text-xs"><SelectValue placeholder="All Countries" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {filterOptions.countries.map(c => (
              <SelectItem key={c.code || c} value={c.code || c}>
                {c.name ? `${c.name} (${c.code})` : c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.os || "all"} onValueChange={v => setFilters({ ...filters, os: v === "all" ? "" : v })}>
          <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="All OS" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All OS</SelectItem>
            {filterOptions.os.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Bundle" value={filters.bundle} onChange={e => setFilters({ ...filters, bundle: e.target.value })} className="w-[180px] h-8 text-xs" />
        <Button size="sm" className="h-8" onClick={generate} disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1" />}Generate
        </Button>
        <Button variant="outline" size="sm" className="h-8" onClick={exportCSV} disabled={!data?.data?.length}>
          <Download className="h-3.5 w-3.5 mr-1" />CSV
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3"><p className="text-[10px] text-muted-foreground uppercase">Total Records</p><p className="text-xl font-bold text-blue-600">{f(stats.total_records)}</p></Card>
          <Card className="p-3"><p className="text-[10px] text-muted-foreground uppercase">Unique Devices</p><p className="text-xl font-bold text-violet-600">{f(stats.unique_devices)}</p></Card>
          <Card className="p-3"><p className="text-[10px] text-muted-foreground uppercase">Countries</p><p className="text-xl font-bold text-emerald-600">{stats.top_countries?.length || 0}</p></Card>
          <Card className="p-3"><p className="text-[10px] text-muted-foreground uppercase">App/Sites</p><p className="text-xl font-bold text-amber-600">{stats.top_bundles?.length || 0}</p></Card>
        </div>
      )}

      {/* Stats Breakdown */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Globe className="h-4 w-4" /> Top Countries</CardTitle></CardHeader>
            <CardContent><div className="space-y-1 max-h-[200px] overflow-y-auto">{stats.top_countries?.map((c, i) => (
              <div key={i} className="flex justify-between text-sm"><span>{c.country}</span><span className="font-mono text-muted-foreground">{f(c.count)}</span></div>
            ))}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Monitor className="h-4 w-4" /> Top OS</CardTitle></CardHeader>
            <CardContent><div className="space-y-1">{stats.top_os?.map((o, i) => (
              <div key={i} className="flex justify-between text-sm"><span>{o.os}</span><span className="font-mono text-muted-foreground">{f(o.count)}</span></div>
            ))}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Smartphone className="h-4 w-4" /> Top Makes</CardTitle></CardHeader>
            <CardContent><div className="space-y-1 max-h-[200px] overflow-y-auto">{stats.top_makes?.map((m, i) => (
              <div key={i} className="flex justify-between text-sm"><span>{m.make}</span><span className="font-mono text-muted-foreground">{f(m.count)}</span></div>
            ))}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Top Bundles</CardTitle></CardHeader>
            <CardContent><div className="space-y-1 max-h-[200px] overflow-y-auto">{stats.top_bundles?.slice(0, 15).map((b, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="truncate max-w-[150px]">{b.bundle}</span><span className="font-mono text-muted-foreground">{f(b.count)}</span></div>
            ))}</div></CardContent>
          </Card>
        </div>
      )}

      {/* Data Table */}
      {data && (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Audience Data ({f(data.total)} records)</CardTitle>
            <div className="flex items-center gap-2 text-xs">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => fetchData(page - 1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
              <span>Page {page} of {data.total_pages}</span>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= data.total_pages} onClick={() => fetchData(page + 1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="px-2 py-2 text-left font-medium">Timestamp</th>
                    <th className="px-2 py-2 text-left font-medium">Device IFA</th>
                    <th className="px-2 py-2 text-left font-medium">Type</th>
                    <th className="px-2 py-2 text-left font-medium">OS</th>
                    <th className="px-2 py-2 text-left font-medium">OSV</th>
                    <th className="px-2 py-2 text-left font-medium">Make</th>
                    <th className="px-2 py-2 text-left font-medium">Model</th>
                    <th className="px-2 py-2 text-left font-medium">IP</th>
                    <th className="px-2 py-2 text-left font-medium">Country</th>
                    <th className="px-2 py-2 text-left font-medium">Region</th>
                    <th className="px-2 py-2 text-left font-medium">City</th>
                    <th className="px-2 py-2 text-left font-medium">Zip</th>
                    <th className="px-2 py-2 text-right font-medium">Lat</th>
                    <th className="px-2 py-2 text-right font-medium">Lon</th>
                    <th className="px-2 py-2 text-left font-medium">Language</th>
                    <th className="px-2 py-2 text-left font-medium">Carrier</th>
                    <th className="px-2 py-2 text-left font-medium">Conn Type</th>
                    <th className="px-2 py-2 text-left font-medium">IP Service</th>
                    <th className="px-2 py-2 text-left font-medium">Inv Type</th>
                    <th className="px-2 py-2 text-left font-medium">Bundle</th>
                    <th className="px-2 py-2 text-left font-medium">App Name</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data?.map((d, i) => (
                    <tr key={i} className="border-b hover:bg-muted/20">
                      <td className="px-2 py-1.5 font-mono whitespace-nowrap">{d.timestamp?.slice(0, 19)}</td>
                      <td className="px-2 py-1.5 font-mono max-w-[200px] truncate">{d.device_ifa}</td>
                      <td className="px-2 py-1.5">{d.device_type}</td>
                      <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{d.os}</Badge></td>
                      <td className="px-2 py-1.5">{d.osv || "—"}</td>
                      <td className="px-2 py-1.5">{d.make}</td>
                      <td className="px-2 py-1.5">{d.model}</td>
                      <td className="px-2 py-1.5 font-mono">{d.ip}</td>
                      <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{d.country}</Badge></td>
                      <td className="px-2 py-1.5">{d.region || "—"}</td>
                      <td className="px-2 py-1.5">{d.city || "—"}</td>
                      <td className="px-2 py-1.5 font-mono">{d.zip || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{d.lat ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{d.lon ?? "—"}</td>
                      <td className="px-2 py-1.5">{d.language || "—"}</td>
                      <td className="px-2 py-1.5">{d.carrier || "—"}</td>
                      <td className="px-2 py-1.5">{d.connectiontype != null ? d.connectiontype : "—"}</td>
                      <td className="px-2 py-1.5">{d.ipservice != null ? d.ipservice : "—"}</td>
                      <td className="px-2 py-1.5">{d.inventory_type}</td>
                      <td className="px-2 py-1.5 max-w-[150px] truncate">{d.bundle}</td>
                      <td className="px-2 py-1.5 max-w-[150px] truncate">{d.app_name}</td>
                    </tr>
                  ))}
                  {(!data.data || data.data.length === 0) && (
                    <tr><td colSpan={21} className="text-center py-12 text-muted-foreground">No audience data for this period</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!stats && !loading && (
        <Card className="py-16">
          <CardContent className="text-center text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">Select period and click "Generate"</p>
            <p className="text-sm mt-1">View device, OS, geo and app data from bid requests</p>
          </CardContent>
        </Card>
      )}
      </TabsContent>

      {/* Spatial Reach API Tab */}
      <TabsContent value="spatial">
        <SpatialReachTab api={api} />
      </TabsContent>

      <TabsContent value="keys">
        <ApiKeysTab api={api} />
      </TabsContent>

      </Tabs>
    </div>
  );
}

function SpatialReachTab({ api }) {
  const [payload, setPayload] = useState(JSON.stringify({
    request_id: "test_" + Date.now(),
    batch_id: "batch_001",
    radii_m: [0, 250, 500, 1000],
    time_windows_days: [7, 15, 30, 60, 90],
    points: [
      { point_id: "p_0001", latitude: 13.7563, longitude: 100.5018, label: "7-Eleven Sukhumvit 21" },
    ],
  }, null, 2));
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const sendRequest = async () => {
    setLoading(true);
    setResponse(null);
    try {
      const parsed = JSON.parse(payload);
      const r = await api.post("/audience/spatial/reach", parsed);
      setResponse(r.data);
    } catch (e) {
      if (e.response?.data) setResponse(e.response.data);
      else toast.error("Invalid JSON or request failed");
    } finally { setLoading(false); }
  };

  const f = (n) => n == null ? "0" : Number(n).toLocaleString();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" /> Spatial Reach Count API</CardTitle>
          <CardDescription>Send bulk lat/lon points with radii and time windows — get unique device counts per combination</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>API Endpoint</Label>
            <code className="block p-2 bg-muted rounded text-sm font-mono">POST /api/audience/spatial/reach</code>
          </div>
          <div className="space-y-2">
            <Label>Request Payload (JSON)</Label>
            <Textarea value={payload} onChange={e => setPayload(e.target.value)} className="font-mono text-xs h-[250px]" data-testid="spatial-payload" />
          </div>
          <Button onClick={sendRequest} disabled={loading} data-testid="spatial-send">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : <><Send className="h-4 w-4 mr-2" />Send Request</>}
          </Button>
        </CardContent>
      </Card>

      {response && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Response
              <Badge variant={response.status === "ok" ? "default" : "destructive"}>{response.status || "error"}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {response.total_points !== undefined && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-muted/30 rounded"><p className="text-[10px] text-muted-foreground uppercase">Total Points</p><p className="text-xl font-bold">{response.total_points}</p></div>
                <div className="p-3 bg-muted/30 rounded"><p className="text-[10px] text-muted-foreground uppercase">Processed</p><p className="text-xl font-bold text-emerald-600">{response.processed_points}</p></div>
                <div className="p-3 bg-muted/30 rounded"><p className="text-[10px] text-muted-foreground uppercase">Failed</p><p className="text-xl font-bold text-red-600">{response.failed_points}</p></div>
                <div className="p-3 bg-muted/30 rounded"><p className="text-[10px] text-muted-foreground uppercase">Time</p><p className="text-xl font-bold text-blue-600">{response.completed_at && response.received_at ? ((new Date(response.completed_at) - new Date(response.received_at)) / 1000).toFixed(2) + "s" : "—"}</p></div>
              </div>
            )}

            {response.results?.map((r, i) => (
              <Card key={i} className={r.status === "error" ? "border-red-300 dark:border-red-900" : ""}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {r.label || r.point_id}
                    <Badge variant={r.status === "ok" ? "outline" : "destructive"} className="text-[10px]">{r.status}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {r.status === "error" ? (
                    <p className="text-sm text-red-600">{r.message}</p>
                  ) : r.cells?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Radius (m)</TableHead>
                          <TableHead>Time Window</TableHead>
                          <TableHead className="text-right">Unique Devices</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {r.cells.map((c, j) => (
                          <TableRow key={j}>
                            <TableCell className="font-mono">{c.radius_m === 0 ? "Exact" : `${c.radius_m}m`}</TableCell>
                            <TableCell>{c.time_window_days} days</TableCell>
                            <TableCell className="text-right font-mono font-medium text-blue-600">{f(c.count)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground">No devices found</p>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Raw JSON */}
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Raw JSON Response</summary>
              <pre className="mt-2 p-3 bg-muted rounded overflow-x-auto font-mono text-[11px] max-h-[300px] overflow-y-auto">{JSON.stringify(response, null, 2)}</pre>
            </details>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function ApiKeysTab({ api }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newKey, setNewKey] = useState(null);

  const fetchKeys = async () => {
    try { const r = await api.get("/audience/api-keys"); setKeys(r.data); }
    catch { } finally { setLoading(false); }
  };
  useState(() => { fetchKeys(); }, []);

  const createKey = async () => {
    if (!newName.trim()) return toast.error("Name is required");
    setCreating(true);
    try {
      const r = await api.post("/audience/api-keys", { name: newName, description: newDesc });
      setNewKey(r.data.key);
      setKeys([r.data, ...keys]);
      setNewName("");
      setNewDesc("");
      toast.success("API key created — copy it now, it won't be shown again!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const deleteKey = async (id) => {
    try {
      await api.delete(`/audience/api-keys/${id}`);
      setKeys(keys.filter(k => k.id !== id));
      toast.success("Key deleted");
    } catch { toast.error("Failed"); }
  };

  const f = (n) => n == null ? "0" : Number(n).toLocaleString();

  return (
    <div className="space-y-4">
      {/* Create Key */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Create API Key</CardTitle>
          <CardDescription>Generate keys for third-party access to the Spatial Reach API</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-3">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Name *</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Partner XYZ" className="h-8" />
            </div>
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Description</Label>
              <Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optional" className="h-8" />
            </div>
            <Button size="sm" className="h-8" onClick={createKey} disabled={creating}>
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Create Key
            </Button>
          </div>
          {newKey && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 rounded-lg space-y-2">
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">New API Key (copy now — won't be shown again!):</p>
              <code className="block p-2 bg-white dark:bg-black rounded font-mono text-sm select-all break-all">{newKey}</code>
              <p className="text-xs text-muted-foreground">Usage: <code>curl -X POST /api/audience/spatial/reach -H "X-API-Key: {newKey.slice(0, 12)}..."</code></p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Keys Table */}
      <Card>
        <CardHeader><CardTitle className="text-base">API Keys</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="text-right">Requests</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map(k => (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs">{k.key}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{k.description || "—"}</TableCell>
                    <TableCell className="text-sm">{k.created_by}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{k.created_at?.slice(0, 10)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{k.last_used?.slice(0, 10) || "Never"}</TableCell>
                    <TableCell className="text-right font-mono">{f(k.request_count)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => deleteKey(k.id)} className="text-destructive hover:text-destructive">Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {keys.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No API keys created yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Usage docs */}
      <Card>
        <CardHeader><CardTitle className="text-base">Usage</CardTitle></CardHeader>
        <CardContent>
          <pre className="p-3 bg-muted rounded text-xs font-mono overflow-x-auto">{`curl -X POST https://YOUR_DOMAIN/api/audience/spatial/reach \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: asrc_YOUR_KEY_HERE" \\
  -d '{
    "request_id": "test_001",
    "radii_m": [0, 250, 500, 1000],
    "time_windows_days": [7, 30, 90],
    "points": [
      {"point_id": "p1", "latitude": 13.7563, "longitude": 100.5018, "label": "Bangkok"}
    ]
  }'`}</pre>
        </CardContent>
      </Card>
    </div>
  );
}
